import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import { useSidebar } from '../../contexts/SidebarContext';
import productionService from '../../services/productionService';
import QRCodeGenerator from './components/QRCodeGenerator';
import QRScanner from './components/QRScanner';
import BatchTrackingPanel from './components/BatchTrackingPanel';
import StatusUpdatePanel from './components/StatusUpdatePanel';
import AlertSystemPanel from './components/AlertSystemPanel';
import QualityControlWorkflow from './components/QualityControlWorkflow';
import { supabase } from '../../lib/supabase';
import { parseLotScanPayload, resolveBatchFromScan } from '../../services/lotScanResolve';
import { isProductionManagerRole } from '../../lib/operatorRoles';

const QRCodeProductionTracking = () => {
  const navigate = useNavigate();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [activeTab, setActiveTab] = useState('generate');
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Production', path: '/production-tracking-dashboard', icon: 'Factory' },
    { label: 'Suivi QR Code', path: '/qr-code-production-tracking', icon: 'QrCode' }
  ];

  // Fetch current user first
  useEffect(() => {
    fetchCurrentUser();
  }, []);

  // Then fetch batches when user is loaded
  useEffect(() => {
    if (currentUser) {
      fetchProductionBatches();
      checkAlerts();
    }
  }, [currentUser]);

  const fetchCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get user profile
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('id, full_name, email')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      // Get user role from user_role_assignments
      const { data: roleAssignment, error: roleError } = await supabase
        .from('user_role_assignments')
        .select(`
          role_id,
          is_active,
          user_roles (
            role_type,
            role_name
          )
        `)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (roleError && roleError.code !== 'PGRST116') {
        console.error('Error fetching role:', roleError);
      }

      console.log('✅ Current user:', profile);
      console.log('✅ User role:', roleAssignment?.user_roles?.role_type);

      setCurrentUser(profile);
      setUserRole(roleAssignment?.user_roles?.role_type || null);
    } catch (err) {
      console.error('Error fetching current user:', err);
    }
  };

  const fetchProductionBatches = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await productionService?.getAll();
      
      console.log('✅ Fetched batches:', data);
      console.log('✅ First batch:', data?.[0]);
      console.log('✅ First batch case_id:', data?.[0]?.case_id);
      
      setBatches(data);
      
      // Check for batches needing alerts
      checkBatchAlerts(data);
    } catch (err) {
      console.error('Error fetching batches:', err);
      setError('Erreur lors du chargement des lots');
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  const checkBatchAlerts = (batchData) => {
    const now = new Date();
    const newAlerts = [];

    batchData?.forEach(batch => {
      const startDate = new Date(batch?.startDate || batch?.start_date);
      const deadline = new Date(batch?.deadline);
      const batchNumber = batch?.batchNumber || batch?.batch_number;
      const status = batch?.status;
      
      const hoursSinceStart = (now - startDate) / (1000 * 60 * 60);
      const daysUntilDeadline = (deadline - now) / (1000 * 60 * 60 * 24);

      // 24-hour processing alert
      if (hoursSinceStart > 24 && status === 'pending') {
        newAlerts?.push({
          id: `alert-24h-${batch?.id}`,
          type: 'warning',
          batchId: batch?.id,
          batchNumber: batchNumber,
          title: 'Délai de traitement dépassé',
          message: `Le lot ${batchNumber} dépasse le délai de 24h pour la revue initiale`,
          timestamp: now
        });
      }

      // 10-day mockup deadline alert
      if (daysUntilDeadline <= 10 && status !== 'completed') {
        newAlerts?.push({
          id: `alert-10d-${batch?.id}`,
          type: daysUntilDeadline <= 2 ? 'error' : 'warning',
          batchId: batch?.id,
          batchNumber: batchNumber,
          title: 'Échéance maquette proche',
          message: `Le lot ${batchNumber} doit être confirmé dans ${Math.ceil(daysUntilDeadline)} jours`,
          timestamp: now
        });
      }
    });

    setAlerts(newAlerts);
  };

  const checkAlerts = () => {
    // Periodic alert check every 5 minutes
    const interval = setInterval(() => {
      if (batches?.length > 0) {
        checkBatchAlerts(batches);
      }
    }, 300000);

    return () => clearInterval(interval);
  };

  const handleQRScan = async (scannedData) => {
    try {
      const parsed = parseLotScanPayload(scannedData);
      const { batch, error } = await resolveBatchFromScan(parsed);
      if (batch?.id) {
        navigate(`/atelier-lot?batch=${batch.id}`);
        toast.success('Ouverture de l’atelier mobile…');
        return;
      }
      const batchNumber = scannedData?.split('-')?.[1];
      const legacy = batches?.find(
        (b) => (b?.batchNumber === batchNumber) || (b?.batch_number === batchNumber)
      );
      if (legacy?.id) {
        navigate(`/atelier-lot?batch=${legacy.id}`);
        toast.success('Lot trouvé');
        return;
      }
      toast.error(error || 'Lot non trouvé');
    } catch (err) {
      console.error('Error processing QR scan:', err);
      toast.error('Erreur lors du scan du QR code');
    }
  };

  const handleStatusUpdate = async (batchId, newStatus, operatorId, notes) => {
    try {
      await productionService?.update(batchId, {
        status: newStatus,
        notes: notes,
        updatedAt: new Date()?.toISOString()
      });
      
      await fetchProductionBatches();
      toast.success('Statut mis à jour');
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleBatchSelect = (batch) => {
    setSelectedBatch(batch);
    setActiveTab('generate');
  };

  const qualityCheckCount = batches?.filter(b => b?.status === 'quality_check')?.length || 0;

  const tabs = [
    { id: 'scan', label: 'Scanner un lot' },
    { id: 'generate', label: 'Génération QR' },
    { id: 'quality', label: 'Contrôle Qualité',  badge: qualityCheckCount },
    { id: 'tracking', label: 'Emballage' },
    { id: 'alerts', label: 'Alertes' }
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
        <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="container mx-auto px-3 sm:px-4 py-4 md:py-6 max-w-7xl min-w-0">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4 md:mb-6">
              <div className="min-w-0">
                <h1 className="text-xl md:text-2xl font-bold text-foreground">Suivi Production QR Code</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Génération, scan et traçabilité des lots de production
                  {isProductionManagerRole(userRole) && (
                    <span className="ml-2 text-primary font-medium">(Mes lots uniquement)</span>
                  )}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Smartphone"
                  onClick={() => navigate('/atelier-lot')}
                  className="min-touch-target w-full md:w-auto"
                >
                  Atelier mobile
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  iconName="RefreshCw"
                  onClick={fetchProductionBatches}
                  disabled={loading}
                  className="min-touch-target w-full md:w-auto"
                >
                  Actualiser
                </Button>
              </div>
            </div>

            {/* Tab Navigation - scrollable on mobile */}
            <div className="bg-card border border-border rounded-lg mb-4 md:mb-6 overflow-hidden">
              <div className="flex overflow-x-auto scrollbar-thin -mb-px">
                {tabs?.map(tab => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 md:px-6 py-3 min-h-[44px] font-medium text-sm border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary bg-muted'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {tab?.label}
                    {tab?.badge > 0 && (
                      <span className="ml-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-error text-error-foreground">
                        {tab?.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : error ? (
              <div className="bg-error/10 border border-error/30 rounded-lg p-4 text-error">
                {error}
              </div>
            ) : (
              <>
                {activeTab === 'generate' && (
                  <QRCodeGenerator
                    batches={batches}
                    selectedBatch={selectedBatch}
                    onBatchSelect={handleBatchSelect}
                    userRole={userRole}
                    currentUser={currentUser}
                  />
                )}

                {activeTab === 'quality' && (
                  <QualityControlWorkflow
                    batches={batches}
                    onRefresh={fetchProductionBatches}
                  />
                )}

                {activeTab === 'scan' && (
                  <QRScanner onScan={handleQRScan} />
                )}

                {activeTab === 'tracking' && (
                  <BatchTrackingPanel
                    batches={batches}
                    onBatchSelect={handleBatchSelect}
                    onRefresh={fetchProductionBatches}
                     userRole={userRole}
                    currentUser={currentUser}
                  />
                )}

                {activeTab === 'status' && (
                  <StatusUpdatePanel
                    batch={selectedBatch}
                    onStatusUpdate={handleStatusUpdate}
                    onBack={() => setActiveTab('tracking')}
                  />
                )}

                {activeTab === 'alerts' && (
                  <AlertSystemPanel
                    alerts={alerts}
                    batches={batches}
                    onBatchSelect={handleBatchSelect}
                       userRole={userRole}
                    currentUser={currentUser}
                  />
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default QRCodeProductionTracking;